<script>
    import { createEventDispatcher } from 'svelte';
    import { link } from "svelte-routing";
    import { deactivateMembership, activateMembership } from "../../../services/subscribersService";
    import { Tooltip } from "@svelte-plugins/tooltips";
    import { format } from "date-fns";
    import { closeAllModals, openModal } from "svelte-modals";
    import AlertModal from "../../Modal/AlertModal.svelte";
    import InputNumberModal from "../../Modal/InputNumberModal.svelte";
    import { isRootManager, isAdmin } from "../../../stores/settingsStore";
    import { subscriberTableFilter } from "../../../stores/subscriberTableStore";
    import toast from "svelte-french-toast";

    const dispatch = createEventDispatcher()
    export let subscriber
    export let maximumPayment;
    export let minimumPaymentUGS;
    export let minimumPaymentNotUGS;
    const maxCharEmail = 20;
    const maxCharName = 12;

    // Carry the active table filters through the edit URL so we can restore
    // them when navigating back to the table after saving
    $: editHref = buildEditHref(subscriber, $subscriberTableFilter);

    function buildEditHref(subscriber, filter) {
        const params = new URLSearchParams({
            id: subscriber.id,
            year: subscriber.memberships[0].year,
        });

        const from = new URLSearchParams();
        if (filter) {
            Object.entries(filter).forEach(([key, val]) => {
                if (val !== null && val !== undefined && val !== "") {
                    from.append(key, val);
                }
            });
        }

        if ([...from].length) {
            params.append("from", from.toString());
        }

        return `/admin/subscribers/edit?${params.toString()}`;
    }

    async function switchSubMembership(isActive, subscriber) {
        if (isActive)  {
            openModal(AlertModal, {
                title: "Vuoi disattivare l'iscrizione?",
                message: `Procedendo disattiverai l'iscrizione di ${subscriber.firstName} ${subscriber.lastName}.`,
                onConfirm: async () => {
                    closeAllModals();

                    await deactivateMembership(subscriber.id, subscriber.memberships[0].year)
                        .then(() => {
                            updateTable("sub deactivate");
                            toast.success("Tesseramento disattivato");
                        })
                        .catch((err) => handleError(err));
                }
            });
        } else {
            openModal(InputNumberModal, {
                title: "Attivazione iscrizione",
                message: `Inserisci la quota per l'attivazione dell'iscrizione di ${subscriber.firstName} ${subscriber.lastName}.`,
                min: subscriber.memberships[0].isUGS ? minimumPaymentUGS : minimumPaymentNotUGS,
                max: maximumPayment,
                onConfirm: async (paidAmount) => {
                    closeAllModals();

                    await activateMembership(subscriber.id, subscriber.memberships[0].year, paidAmount)
                        .then(() => {
                            updateTable("sub activate");
                            toast.success("Tesseramento attivato");
                        })
                        .catch((err) => handleError(err))
                }
            });
        }
    }

    function handleError(err) {
        if(err?.response?.status === 404) {
            toast.error("Iscrizione non trovata");
            return;
        }

        if(err?.response?.status === 500) {
            toast.error(`Errore durante la modifica dell'iscrizione, errore ${err.toString()}`);
        }
    }

    function updateTable(status) {
        dispatch("update", { state: status });
    }

    async function copyEmail() {
        try {
            await navigator.clipboard.writeText(subscriber.email);
            toast.success("Email copiata");
        } catch (err) {
            toast.error("Impossibile copiare l'email");
        }
    }

    function sendEmail() {
        window.location.href = `mailto:${subscriber.email}`;
    }

    function getAddressTooltip(address) {
        return `<ul>
            <li>${address.country}</li>
            <li>${address.city} ${address?.province ?? ''}</li>
            <li>${address.street} ${address.number}</li>
            ${ !!address?.secondary ? `<li>${address.secondary}</li>` : '' }
        </ul>`
    }

</script>

<tr class="h-14 border-b">
    <td>
        <a use:link
           href={editHref}
           class="mr-3"
        >
            <span class="w-10 h-10 text-sm border inline-flex items-center justify-center rounded-full cursor-pointer">
                <i class="fa fa-edit"></i>
            </span>
        </a>
    </td>

    <td class="{subscriber.firstName.length > maxCharName ? 'scroll-container' : ''}">
        <div class="{subscriber.firstName.length > maxCharName ? 'scroll-text' : ''}">
            {subscriber.firstName}
        </div>
    </td>
    <td class="{subscriber.lastName.length > maxCharName ? 'scroll-container' : ''}">
        <div class="{subscriber.lastName.length > maxCharName ? 'scroll-text' : ''}">
            {subscriber.lastName}
        </div>
    </td>
    <td class="{subscriber.email.length > maxCharEmail ? 'scroll-container' : ''}">
        <span class="{subscriber.email.length > maxCharEmail ? 'scroll-text' : ''}">
            {subscriber.email}
        </span>

        <i class="fa fa-copy text-lightBlue-500 ml-3 cursor-pointer" on:click={copyEmail}></i>
<!--        <i class="fa fa-share-square ml-3 cursor-pointer" on:click={sendEmail}></i>-->
    </td>
    <td>
        <a href="tel:{subscriber.phone.code}{subscriber.phone.number}">
            {subscriber.phone.code} {subscriber.phone.number}
        </a>
    </td>
    <td class="cursor-pointer font-450">
        <div class="truncate max-w-[120px]">
            <Tooltip maxWidth="500" content={getAddressTooltip(subscriber.residenceAddress)}>
                {subscriber.residenceAddress.city}

                {#if subscriber.residenceAddress.province}- {subscriber.residenceAddress.province}{/if}
            </Tooltip>
        </div>
    </td>
    <td>{format(new Date(subscriber.memberships[0].createdAt), "dd/MM/yyyy")}</td>
    <td class="h-14 flex justify-center items-center select-none { ($isAdmin || $isRootManager)  ? 'cursor-pointer hover:shadow-md' : ''}"
        on:click|preventDefault={($isRootManager || $isAdmin) ? switchSubMembership(subscriber.memberships[0]?.active, subscriber) : () => {}}
    >
        {subscriber.memberships[0]?.active === true ? "ATTIVO" : "NON ATTIVO" }
    </td>
    <td>{subscriber.memberships[0]?.paidAt ? format(new Date(subscriber.memberships[0].paidAt), "dd/MM/yyyy") : ""}</td>
    <td>
        <div class="flex items-center">
            {#if subscriber.memberships[0].isUGS}
                <i class="fa fa-check fa-1x"></i>
            {/if}
        </div>
    </td>
    <td>{subscriber.memberships[0]?.paidAmount ? `${subscriber.memberships[0]?.paidAmount} €` : ""}</td>
</tr>